import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR, todayISO } from "@/lib/api";
import StatCard from "@/components/StatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import PeriodSelector, { rangeFromValue } from "@/components/PeriodSelector";

const expenseTotal = (e) =>
  (e.expenses || []).reduce((a, b) => a + (Number(b.amount) || 0), 0);

/**
 * Reusable ledger page for a single numeric metric per-day per-vehicle.
 *
 * config: {
 *   key: string  ("sales" | "deposit" | "earnings" | "profit")
 *   title, eyebrow, subtitle, accent (tailwind text class), color (hex for line),
 *   getValue: (entry) => number
 * }
 */
export default function MetricLedger({ config }) {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("__all");
  const [period, setPeriod] = useState({ mode: "last", n: 3 });
  const [entries, setEntries] = useState([]);

  const { start, end } = useMemo(() => rangeFromValue(period), [period]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  useEffect(() => {
    const params = { start, end };
    if (vehicleId !== "__all") params.vehicle_id = vehicleId;
    api.get("/entries", { params }).then((r) => setEntries(r.data));
  }, [vehicleId, start, end]);

  const vehicleMap = useMemo(() => {
    const m = {};
    vehicles.forEach((v) => (m[v.id] = v));
    return m;
  }, [vehicles]);

  const rows = useMemo(() => {
    const out = entries
      .map((e) => ({
        date: e.date,
        vehicleId: e.vehicle_id,
        vehicleName: vehicleMap[e.vehicle_id]?.name || "—",
        vehicleReg: vehicleMap[e.vehicle_id]?.registration_number || "",
        amount: config.getValue(e),
        remark: config.getRemark ? config.getRemark(e) : (e.remark || ""),
        is_halt: e.is_halt,
      }))
      .filter((r) => r.amount !== 0);
    out.sort((a, b) => (a.date < b.date ? 1 : -1));
    return out;
  }, [entries, vehicleMap, config]);

  const total = rows.reduce((a, b) => a + (b.amount || 0), 0);
  const count = rows.length;
  const avg = count ? total / count : 0;
  const best = rows.reduce((m, r) => (r.amount > (m?.amount ?? -Infinity) ? r : m), null);

  const series = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      map[r.date] = (map[r.date] || 0) + r.amount;
    });
    return Object.entries(map)
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [rows]);

  const byVehicle = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.vehicleId]) {
        map[r.vehicleId] = {
          name: r.vehicleName,
          reg: r.vehicleReg,
          total: 0,
          days: 0,
        };
      }
      map[r.vehicleId].total += r.amount;
      map[r.vehicleId].days += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [rows]);

  return (
    <div className="space-y-6" data-testid={`${config.key}-page`}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">{config.eyebrow}</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            {config.title}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{config.subtitle}</p>
        </div>
      </div>

      <div className="card-swiss p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger
                className="mt-1"
                data-testid={`${config.key}-vehicle-select`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} · {v.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Period</Label>
            <div className="mt-1">
              <PeriodSelector
                value={period}
                onChange={setPeriod}
                testid={`${config.key}-period`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={config.totalLabel || `Total ${config.title}`}
          value={formatINR(total)}
          accent={config.accent}
          testid={`${config.key}-total`}
        />
        <StatCard
          label={config.daysLabel || "Days"}
          value={count}
          sub={`${start} → ${end}`}
          testid={`${config.key}-count`}
        />
        <StatCard
          label="Avg per Day"
          value={formatINR(avg)}
          testid={`${config.key}-avg`}
        />
        <StatCard
          label="Best Day"
          value={best ? formatINR(best.amount) : "—"}
          sub={best ? `${best.date} · ${best.vehicleName}` : ""}
          accent={config.accent}
          testid={`${config.key}-best`}
        />
      </div>

      {series.length > 0 && (
        <div className="card-swiss p-5" data-testid={`${config.key}-chart`}>
          <div className="label-eyebrow mb-2">Trend</div>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">
            Daily {config.title}
          </h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  formatter={(v) => formatINR(v)}
                  contentStyle={{ borderRadius: 6, border: "1px solid #e5e7eb" }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={config.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name={config.title}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {byVehicle.length > 0 && vehicleId === "__all" && (
        <div className="card-swiss p-5" data-testid={`${config.key}-by-vehicle`}>
          <div className="label-eyebrow mb-2">Breakdown</div>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">
            By Vehicle
          </h3>
          <div className="space-y-2">
            {byVehicle.map((v) => {
              const pct = total > 0 ? (v.total / total) * 100 : 0;
              return (
                <div
                  key={v.name + v.reg}
                  className="border border-gray-200 rounded-md p-3"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{v.name}</div>
                      <div className="text-[11px] text-gray-500 font-mono">
                        {v.reg} · {v.days} day{v.days !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className={`font-mono font-bold ${config.accent}`}>
                      {formatINR(v.total)}
                    </div>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full"
                      style={{ width: `${pct}%`, background: config.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card-swiss p-5" data-testid={`${config.key}-list`}>
        <div className="label-eyebrow mb-3">All entries</div>
        {rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            No {config.title.toLowerCase()} recorded in this range.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-4 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Vehicle</th>
                  <th className="py-2 px-2 font-semibold">Remark</th>
                  <th className="py-2 px-2 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 hover-row"
                    data-testid={`${config.key}-row-${i}`}
                  >
                    <td className="py-2 pr-4 font-mono text-gray-700">
                      {r.date}
                      {r.is_halt && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1 py-0.5 rounded-sm">
                          halt
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <div className="font-semibold">{r.vehicleName}</div>
                      <div className="text-[11px] text-gray-500 font-mono">
                        {r.vehicleReg}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      {r.remark || <span className="text-gray-400">—</span>}
                    </td>
                    <td
                      className={`py-2 px-2 text-right font-mono font-semibold ${config.accent}`}
                    >
                      {formatINR(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td className="py-3 pr-4 font-bold uppercase tracking-wider text-xs" colSpan={3}>
                    Total
                  </td>
                  <td className={`py-3 px-2 text-right font-mono font-bold ${config.accent}`}>
                    {formatINR(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export const SALES_CFG = {
  key: "sales",
  title: "Total Sales",
  totalLabel: "Total Sales",
  daysLabel: "Run Days",
  eyebrow: "Inflow",
  subtitle: "Daily sales recorded across all vehicles.",
  accent: "text-black",
  color: "#0a0a0a",
  getValue: (e) => Number(e.sales) || 0,
  getRemark: (e) => e.remark || "",
};

export const DEPOSIT_CFG = {
  key: "deposit",
  title: "Syndicate Deposit",
  totalLabel: "Total Deposit",
  daysLabel: "Days",
  eyebrow: "Reserve",
  subtitle: "Daily syndicate deposits across all vehicles.",
  accent: "text-blue-700",
  color: "#1d4ed8",
  getValue: (e) => Number(e.deposit) || 0,
  getRemark: (e) => e.remark || "",
};

export const EARNINGS_CFG = {
  key: "earnings",
  title: "Net Earnings",
  totalLabel: "Total Earnings",
  daysLabel: "Run Days",
  eyebrow: "Inflow",
  subtitle: "Net earnings recorded each day.",
  accent: "text-emerald-700",
  color: "#16a34a",
  getValue: (e) => Number(e.net_earnings) || 0,
  getRemark: (e) => e.remark || "",
};

export const EXTRA_INCOME_CFG = {
  key: "extra-income",
  title: "Extra Income",
  totalLabel: "Total Extra Income",
  daysLabel: "Days",
  eyebrow: "Inflow",
  subtitle: "Additional income (parcels, ads, etc.) recorded on daily entries.",
  accent: "text-emerald-700",
  color: "#059669",
  getValue: (e) => Number(e.extra_income) || 0,
  getRemark: (e) => e.extra_income_remark || "",
};

export const PROFIT_CFG = {
  key: "profit",
  title: "Profit",
  totalLabel: "Total Profit",
  daysLabel: "Days",
  eyebrow: "Bottom line",
  subtitle: "Daily profit (Net Earnings + Extra Income − Expenses).",
  accent: "text-emerald-700",
  color: "#0a0a0a",
  getValue: (e) =>
    (Number(e.net_earnings) || 0) +
    (Number(e.extra_income) || 0) -
    expenseTotal(e),
  getRemark: (e) => e.remark || "",
};
